import type { CmsPage, CtaComponentProps, HeaderAction } from "@/cms/models";
import {
  findMenuPage,
  linkFor,
  pageLink,
  type ResolvedLink,
} from "@/utils/header-navigation";
import { record } from "@/utils/colour-values";
import { fillChangesInDark, ownColourCss } from "@/utils/colour-versions";
import type { HeaderSurfaces } from "@/utils/header-colours";
import { isLookSystem } from "@/utils/look-gate";

export type HeaderActionStyle = "primary" | "outline" | "text" | "icon";
export type HeaderActionMobile = "header" | "bottomBar" | "menu" | "hidden";

export interface ResolvedHeaderAction {
  key: string;
  id: string;
  type: HeaderAction["type"];
  label: string;
  link?: ResolvedLink;
  dialogTargetId?: string;
  style: HeaderActionStyle;
  mobile: HeaderActionMobile;
  /** Colour, surface and typography fields passed through from `CtaComponentProps`. */
  config: Partial<CtaComponentProps>;
}

const styles: HeaderActionStyle[] = ["primary", "outline", "text", "icon"];
const placements: HeaderActionMobile[] = [
  "header",
  "bottomBar",
  "menu",
  "hidden",
];

const text = (value: unknown) =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

/** `layout.header.actions` present, even as `[]`, replaces the type's own action area. */
export function headerActionsEnabled(
  header: { actions?: unknown } | null | undefined,
) {
  return Array.isArray(header?.actions);
}

/** The customer universe link exactly as the Standard header builds it today. */
export function customerPortalUrl(
  b2cLink: string | undefined | null,
  appData:
    | { resourceGroup?: { primaryB2CApplication?: { url?: string } } }
    | undefined,
) {
  const url = b2cLink ?? appData?.resourceGroup?.primaryB2CApplication?.url;
  if (!url) return undefined;
  return url.startsWith("https://")
    ? url
    : "https://" + url.replace("http://", "");
}

/** Each of an action's states: its fill and the colours drawn on it. */
const ACTION_STATES = [
  { fill: "bgColor", on: ["textColor", "borderColor"] },
  { fill: "hoverBgColor", on: ["hoverTextColor", "hoverBorderColor"] },
  { fill: "activeBgColor", on: ["activeTextColor", "activeBorderColor"] },
] as const;
const FILLED_STYLES: HeaderActionStyle[] = ["primary", "outline"];

/**
 * An action's own colours with their dark versions on the look system, as HeaderActionItem draws them: each fill takes
 * its version, and a state's text and border where the fill under them changes in dark: that state's fill where it is
 * painted, else the action's own (`surface.backgroundColor` or `bgColor`, painted for a primary or outline action, a
 * primary one in the button colour without it), else the header bar, and in the menu the panel too. A text or icon
 * action paints neither, so its hover lies on the bar (.re-action-quiet:hover). A bottom-bar action lies in the bar
 * on desktop and in the bottom bar on mobile, which paints its `bgColor` and its states' over it whatever its style,
 * else the button colour.
 */
function actionInDark(
  theme: unknown,
  style: HeaderActionStyle,
  mobile: HeaderActionMobile,
  config: Partial<CtaComponentProps>,
  surfaces: HeaderSurfaces,
): Partial<CtaComponentProps> {
  const colours = { ...config } as Record<string, unknown>;
  const surface = record(colours.surface);
  const filled = (value: unknown): value is string =>
    typeof value === "string" && value.trim() !== "";
  const header =
    surfaces.bar &&
    (mobile !== "menu" || surfaces.panel) &&
    (mobile !== "bottomBar" ||
      !filled(colours.bgColor) ||
      fillChangesInDark(theme, colours.bgColor));
  const painted = FILLED_STYLES.includes(style);
  const ownFill = !painted
    ? undefined
    : colours.surface
      ? (surface.backgroundColor ?? colours.bgColor)
      : colours.bgColor;
  const baseUnder = filled(ownFill)
    ? fillChangesInDark(theme, ownFill)
    : (painted && !!colours.surface) || style === "primary" || header;
  const statesPainted = [
    painted,
    ...(mobile === "bottomBar"
      ? [!colours.surface && filled(colours.bgColor)]
      : []),
  ];
  for (const { fill, on } of ACTION_STATES) {
    const value = colours[fill];
    if (filled(value)) colours[fill] = ownColourCss(theme, value);
    // .re-nav-action-outline:hover paints --button-hover-bg-color where the action sets no hover fill.
    const rolePainted =
      fill === "hoverBgColor" && style === "outline" && !filled(value);
    const under = rolePainted
      ? true
      : fill === "bgColor" || !filled(value) || !statesPainted.some(Boolean)
        ? baseUnder
        : fillChangesInDark(theme, value) &&
          (statesPainted.every(Boolean) || baseUnder);
    if (!under) continue;
    for (const key of on) {
      const colour = colours[key];
      if (filled(colour)) colours[key] = ownColourCss(theme, colour);
    }
  }
  if (colours.surface && typeof colours.surface === "object")
    colours.surface = {
      ...surface,
      ...(filled(surface.backgroundColor)
        ? { backgroundColor: ownColourCss(theme, surface.backgroundColor) }
        : {}),
      ...(filled(surface.borderColor) && baseUnder
        ? { borderColor: ownColourCss(theme, surface.borderColor) }
        : {}),
    };
  return colours as Partial<CtaComponentProps>;
}

const phoneHref = (phone: string) => `tel:${phone.replace(/[^\d+]/g, "")}`;

/** On the look system `theme` and the header's `surfaces` give the actions' own colours their dark versions. */
export function resolveHeaderActions(
  actions: unknown,
  context: {
    pages: CmsPage[];
    portalUrl?: string;
    theme?: unknown;
    surfaces?: HeaderSurfaces;
  },
): ResolvedHeaderAction[] {
  if (!Array.isArray(actions)) return [];
  let links = 0;
  let bottomBar = false;
  return actions.flatMap((raw, index): ResolvedHeaderAction[] => {
    if (typeof raw !== "object" || raw === null) return [];
    const action = raw as HeaderAction;
    const {
      id,
      type,
      label: rawLabel,
      link: rawLink,
      pageId,
      action: dialog,
      phone,
      style: rawStyle,
      mobile: rawMobile,
      openInNewTab,
      ...config
    } = action;
    let label = text(rawLabel);
    let link: ResolvedLink | undefined;
    let dialogTargetId: string | undefined;
    if (type === "link") {
      if (dialog?.type === "dialog" && text(dialog.targetId))
        dialogTargetId = dialog.targetId;
      else {
        const page = findMenuPage(context.pages, pageId, rawLink);
        const url = text(rawLink);
        link = page
          ? pageLink(page, openInNewTab)
          : url
            ? linkFor(url, !!openInNewTab)
            : undefined;
      }
      if (!label || (!link && !dialogTargetId)) return [];
    } else if (type === "phone") {
      const number = text(phone);
      if (!label || !number) return [];
      link = { href: phoneHref(number), external: true, newTab: false };
    } else if (type === "login") {
      if (!context.portalUrl) return [];
      label ??= "Kundeunivers";
      link = { href: context.portalUrl, external: true, newTab: true };
    } else if (type === "search") {
      label ??= "Søg";
    } else return [];
    const isLink = type === "link";
    const style =
      styles.find((value) => value === rawStyle) ??
      (isLink
        ? links === 0
          ? "primary"
          : "text"
        : type === "phone"
          ? "text"
          : "icon");
    if (isLink) links++;
    let mobile =
      placements.find((value) => value === rawMobile) ??
      (isLink ? "menu" : "header");
    if (mobile === "bottomBar") {
      if (bottomBar) mobile = "menu";
      bottomBar = true;
    }
    return [
      {
        key: `${index}`,
        id: text(id) ?? `${index}`,
        type,
        label,
        link,
        dialogTargetId,
        style,
        mobile,
        config: isLookSystem(context.theme)
          ? actionInDark(
              context.theme,
              style,
              mobile,
              config,
              context.surfaces ?? { bar: false, panel: false },
            )
          : config,
      },
    ];
  });
}
